// src/components/PremiumReportProcessing.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, CheckCircle, Lightbulb } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip } from '@fortawesome/free-solid-svg-icons';
import './PremiumReportProcessing.css'; // We will use the new unified CSS

const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
// Add the counselling API base for fetching facts
const REACT_APP_COUNSELLING_API_BASE = process.env.REACT_APP_COUNSELLING_API_BASE;

const PremiumReportProcessing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reportId = searchParams.get('id');

  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');

  // Timer state
  const TOTAL_SECONDS = 298; // Initial time for this report
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);

  // State for loading facts
  const [facts, setFacts] = useState([]);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [factIsVisible, setFactIsVisible] = useState(true);

  // Effect for the countdown timer
  useEffect(() => {
    if (status === 'processing') {
      if (secondsLeft <= 0) return;
      const timerId = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
      return () => clearTimeout(timerId);
    }
  }, [secondsLeft, status]);

  // Effect to fetch loading facts (reused from the first component)
  useEffect(() => {
    const fetchFacts = async () => {
      try {
        const { data } = await axios.get(`${REACT_APP_COUNSELLING_API_BASE}/api/loading-facts`);
        if (data && data.length > 0) {
          setFacts(data);
        }
      } catch (error) {
        console.error("Failed to fetch loading facts:", error);
      }
    };
    fetchFacts();
  }, []);

  // Effect to cycle through facts with a fade animation (reused)
  useEffect(() => {
    if (facts.length > 0 && status === 'processing') {
      const interval = setInterval(() => {
        setFactIsVisible(false); // Start fade-out
        setTimeout(() => {
          setCurrentFactIndex(prevIndex => (prevIndex + 1) % facts.length);
          setFactIsVisible(true); // Start fade-in
        }, 500);
      }, 7000);
      return () => clearInterval(interval);
    }
  }, [facts, status]);

  // Effect for polling the premium report status (specific to this component)
  useEffect(() => {
    if (!reportId) {
      setStatus('failed');
      setErrorMessage('No report ID found in the URL.');
      return;
    }

    const checkStatus = async () => {
      try {
        const { data: statusRes } = await axios.get(`${REACT_APP_API_BASE}/api/reports/status/${reportId}`);
        // Check for multiple possible status keys for robustness
        if (statusRes.state === 'completed' || statusRes.status === 'completed') {
          setStatus('completed');
          // Redirect immediately on completion
          navigate(`/dashboard?view=report-premium&id=${reportId}`);
          return 'stop'; // Signal to stop polling
        } else if (statusRes.state === 'failed' || statusRes.status === 'failed') {
          setErrorMessage(statusRes.failedReason || statusRes.errorMessage || 'Report generation failed.');
          setStatus('failed');
          return 'stop'; // Signal to stop polling
        }
      } catch (err) {
        setErrorMessage(err.response?.data?.message || 'Could not connect to the server.');
        setStatus('failed');
        return 'stop'; // Signal to stop polling
      }
    };

    const intervalId = setInterval(async () => {
      const signal = await checkStatus();
      if (signal === 'stop') {
        clearInterval(intervalId);
      }
    }, 5000);
    checkStatus(); // Initial check

    return () => clearInterval(intervalId);
  }, [reportId, navigate]);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  
  const renderContent = () => {
    switch (status) {
      case 'completed': // This case will be brief as redirection happens quickly
        return (
          <>
            <CheckCircle className="status-icon success" size={80} />
            <h2 className="processing-title">Report Ready!</h2>
            <p className="processing-subtitle">Redirecting you to the results...</p>
          </>
        );
      case 'failed':
        return (
          <>
            <AlertTriangle className="status-icon error" size={80} />
            <h2 className="processing-title">Generation Failed</h2>
            <div className="error-message">Details: {errorMessage}</div>
            <Link to="/dashboard" className="nav-button nav-button-primary">Back to Dashboard</Link>
          </>
        );
      case 'processing':
      default:
        return (
          <>
            <div className="processing-animation-container">
              <Loader2 className="status-icon loading" size={96} />
              <FontAwesomeIcon icon={faMicrochip} className="processing-main-icon" />
            </div>
            <h2 className="processing-title">Your  Report is Generating...</h2>
            <p className="processing-subtitle">GNVISA is analyzing multiple colleges for you. This may take a few minutes.</p>
            {facts.length > 0 && (
              <div className={`loading-fact ${factIsVisible ? 'visible' : ''}`}>
                <Lightbulb className="fact-icon" size={20} />
                <p>{facts[currentFactIndex]?.text}</p>
              </div>
            )}
            <div className="timer-display">
              Estimated time remaining: {formatTime(secondsLeft)}
            </div>
            <p className="processing-footer">Please keep this page open. You will be redirected automatically.</p>
          </>
        );
    }
  };

  return (
    <div className="processing-container">
      <div className="processing-card">
        {renderContent()}
        {reportId && <div className="report-id-display-bottom">Report ID: {reportId}</div>}
      </div>
    </div>
  );
};

export default PremiumReportProcessing;